import type { OrderCancellationCode } from "@loot-os/core/db/schema";
import {
  isTargetRefusedForType,
  isTerminalOrderStatus,
  ORDER_TRANSITIONS,
} from "@loot-os/fulfillment-core/order/state-machine";
import {
  MAX_CANCELLATION_REASON,
  type OrderLineStatus,
  type OrderStatus,
  type OrderType,
} from "@loot-os/fulfillment-core/order/types";

import { OPEN_IN_TABS_LABEL } from "./orderTabs";
/**
 * Statuses the transition buttons offer. CANCELLED is excluded because the
 * transition procedure refuses it; cancelling goes through orders.cancel. So
 * is a target the order's type refuses, such as FAIL on a WITHDRAW_ASSET order.
 */
export function transitionTargets(
  status: OrderStatus,
  type: OrderType,
): Exclude<OrderStatus, "CANCELLED">[] {
  return ORDER_TRANSITIONS[status].filter(
    (target): target is Exclude<OrderStatus, "CANCELLED"> =>
      target !== "CANCELLED" && !isTargetRefusedForType(type, target),
  );
}

/**
 * Statuses a ship-side move reaches. TCG Fulfill Order's one Ship Order button made both (no
 * tracking number preshipped, one shipped), and its packing rule held both back.
 */
const SHIP_TARGETS: readonly OrderStatus[] = ["PRESHIPPED", "SHIPPED"];

/**
 * TCG Fulfill Order's Ship Order rule: a TCG order with a line still PENDING cannot be
 * preshipped or shipped from the page. The server does not refuse it (a ship packs what is left),
 * so this is the page's check alone, as it was in Retool, and TCG alone, as it was there.
 */
export function packingHoldsShipment(
  order: { type: OrderType },
  lines: ReadonlyArray<PackableLineFacts>,
): boolean {
  return order.type === "TCG" && hasPendingPackableLine(lines);
}

/** What {@link isPackableLine} and the pending-line rules read of a line. */
export interface PackableLineFacts {
  status: OrderLineStatus;
  stockReserved: boolean | null;
}

/**
 * Some line is still PENDING and can be packed here. A record-only line is packed in the system
 * that owns it, so it never holds a page action back: the page offers no way to pack it.
 */
export function hasPendingPackableLine(lines: ReadonlyArray<PackableLineFacts>): boolean {
  return lines.some((line) => line.status === "PENDING" && isPackableLine(line));
}

/**
 * Whether the page offers packing on this order: while it is open, the server's rule
 * (`updateOrderLines`). Retool's own rule refused only a CANCELLED order, but in LOOT_OS mode its
 * pack reaches loot-os first and is held to this one, so this is what Retool allows today. A
 * PRESHIPPED order with a card still PENDING can be packed, so its ship is never stuck.
 */
export function isPackingOpen(status: OrderStatus): boolean {
  return !isTerminalOrderStatus(status);
}

/** Whether a move to `target` is held back by {@link packingHoldsShipment}. */
export function isShipTarget(target: OrderStatus): boolean {
  return SHIP_TARGETS.includes(target);
}

/**
 * TCG Fulfill Order's one ship button, which opens the Ship dialog: "Ship" while the order can
 * still preship, "Add Tracking" once it is PRESHIPPED, and none where SHIPPED is not a target
 * (Retool disabled it on PENDING, SHIPPED and CANCELLED, none of which can ship).
 */
export function shipButtonLabel(
  status: OrderStatus,
  targets: readonly OrderStatus[],
): "Ship" | "Add Tracking" | null {
  if (!targets.includes("SHIPPED")) return null;
  return status === "PRESHIPPED" ? "Add Tracking" : "Ship";
}

/**
 * Where the Ship dialog's confirm moves the order, as Retool's does: a tracking number ships it,
 * a blank box preships it (adminPreshipTcgOrder), and on a PRESHIPPED order a blank box does
 * nothing, so the confirm waits for a number. `trackingNumber` is the one the ship would keep,
 * the saved number when there is one.
 */
export function shipDialogTarget(args: {
  targets: readonly OrderStatus[];
  trackingNumber: string;
}): "SHIPPED" | "PRESHIPPED" | null {
  if (args.trackingNumber.trim()) return args.targets.includes("SHIPPED") ? "SHIPPED" : null;
  return args.targets.includes("PRESHIPPED") ? "PRESHIPPED" : null;
}

/**
 * Whether the page may pack or unpack a line. A cancelled line is final, and a record-only line
 * (`stockReserved === false`) is one a tenant mirrors from its own system, boxed's while it still
 * owns its orders: it is packed there and the pack is mirrored here, and a pack made here would
 * never reach boxed's own record.
 */
export function isPackableLine(line: PackableLineFacts): boolean {
  return line.status !== "CANCELLED" && line.stockReserved !== false;
}

/** The causes a fulfiller picks from. TENANT_CANCELLED is written by the tenant API alone. */
export const FULFILLER_CANCELLATION_CODES = [
  "PRICE_EXCEEDED",
  "OUT_OF_STOCK",
] as const satisfies readonly `${OrderCancellationCode}`[];

export type FulfillerCancellationCode = (typeof FULFILLER_CANCELLATION_CODES)[number];

/** What the cancel form says about a note the mutation would refuse, or undefined when it holds. */
export function cancellationReasonError(reason: string): string | undefined {
  const trimmed = reason.trim();
  if (!trimmed) return "Say why you are cancelling.";
  if (trimmed.length > MAX_CANCELLATION_REASON) {
    return `Keep the note to ${MAX_CANCELLATION_REASON} characters.`;
  }
  return undefined;
}

/** What the cancel form says about a customer message the mutation would refuse. */
export function customerMessageError(message: string): string | undefined {
  if (message.trim().length > MAX_CANCELLATION_REASON) {
    return `Keep the message to ${MAX_CANCELLATION_REASON} characters.`;
  }
  return undefined;
}

/**
 * Reads the cancel form's bonus field: blank is no bonus, otherwise a percent from 0 to 100 (boxed's
 * cancellationBonusPercent, offered on MARKETPLACE orders only).
 */
export function parseCancellationBonus(
  text: string,
): { bonusPercent: number | undefined } | { error: string } {
  const trimmed = text.trim();
  if (!trimmed) return { bonusPercent: undefined };
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < 0 || value > 100) {
    return { error: "Give a bonus from 0 to 100 percent, or leave it blank." };
  }
  return { bonusPercent: value };
}

/**
 * The order page's actions that have an enable rule (TCG Fulfill Order's buttons): Edit Address,
 * and Generate Label and Upload Image, placed but not built yet. Their rules live here, not in the
 * page, so they are tested as a matrix and a live action's server refusal can be tested against
 * the same one. Notes, cost and tracking edit at any status, so they have none.
 */
export type OrderPageAction = "editAddress" | "generateLabel" | "uploadImage";

/** Why an order page action is off; one member per cause, as the page could say it. */
export type OrderActionBlock =
  /** Retool's label flow only runs while the order is being worked: PROCESSING or PRESHIPPED. */
  | "status"
  /** A label was already bought: the order has a tracking number. */
  | "has_tracking"
  /** Never ship to a locked account. */
  | "account_locked"
  /** A TCG order with a card still pending is not packed yet. */
  | "pending_tcg_line";

/** What the order page's enable rules read. */
export interface OrderActionFacts {
  status: OrderStatus;
  type: OrderType;
  trackingNumber: string | null;
  accountLocked: boolean;
  /** Some line can still be packed here and is PENDING (`hasPendingPackableLine`). */
  hasPendingLine: boolean;
}

/** The statuses a label may be bought in. Retool's rule was a blocklist over its five statuses. */
const LABEL_STATUSES: ReadonlySet<OrderStatus> = new Set(["PROCESSING", "PRESHIPPED"]);

/**
 * Why `action` is off for this order, or null when it is on. Retool's TCG Fulfill Order rules,
 * written as allowlists: Retool's blocklists named its five statuses and would leave Generate
 * Label on for HOLD (under review), UNAPPROVED, APPROVED, SUCCESS and FAIL.
 */
export function orderActionBlock(
  action: OrderPageAction,
  facts: OrderActionFacts,
): OrderActionBlock | null {
  if (action === "editAddress") return isTerminalOrderStatus(facts.status) ? "status" : null;
  if (action !== "generateLabel") return null;
  if (!LABEL_STATUSES.has(facts.status)) return "status";
  if (facts.trackingNumber) return "has_tracking";
  if (facts.accountLocked) return "account_locked";
  if (facts.type === "TCG" && facts.hasPendingLine) return "pending_tcg_line";
  return null;
}

/** The order list's selection buttons (TCG Order List), in Retool's order down the column. */
export const ORDER_LIST_SELECTION_ACTIONS = [
  "Open Combined Card View",
  "Open in Tabs",
  "Export Pull Sheet",
  "Export Orders",
  "Export Packing Slips",
  "Export Thermal Labels",
] as const;

/** What a selection button says: its name, and Open in Tabs adds Retool's pop-up reminder. */
export function selectionActionLabel(
  action: (typeof ORDER_LIST_SELECTION_ACTIONS)[number],
): string {
  return action === "Open in Tabs" ? OPEN_IN_TABS_LABEL : action;
}

/** A selection button acts on the selected rows, so it is off with none selected. */
export function isSelectionActionEnabled(selectedCount: number): boolean {
  return selectedCount > 0;
}
